import React, { useState, useEffect } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, TextField, Button,
  useTheme, useMediaQuery, Card, CardContent, Divider, Chip, IconButton
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import API from "../API/API";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";

const api = new API();

const formatUserName = (name) => {
  if (!name) return "";
  const lower = name.toLowerCase().trim();
  if (lower === "ramesh admin") {
    return "ramesh Admin";
  }
  return name.replace(/ramesh/gi, "ramesh");
};

export default function AdvanceDataReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activePeriod, setActivePeriod] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    fetchAdvanceData();
  }, [selectedDate]);

  const fetchAdvanceData = async (period = activePeriod) => {
    setLoading(true);
    try {
      let url = "";
      if (period) {
        url = `api/advances?period=${period}`;
      } else {
        const dateStr = dayjs(selectedDate).format("YYYY-MM-DD");
        url = `api/advances?date=${dateStr}`;
      }
      const res = await api.get(url);
      setData(res?.data?.data || res?.data || []);
    } catch (e) {
      toast.error("Failed to fetch advance data");
    } finally {
      setLoading(false);
    }
  };

  const tableHeaderStyle = {
    bgcolor: "#0b1120",
    color: "#00d7d7",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: "bold",
    border: "1px solid #1c2b4d",
    textAlign: "center",
    whiteSpace: "nowrap",
    py: 1,
    px: isMobile ? 1 : 2
  };

  const cellStyle = {
    color: "#fff",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: "500",
    border: "1px solid #1c2b4d",
    textAlign: "center",
    whiteSpace: "nowrap",
    py: 0.5,
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2, bgcolor: "#111C31", minHeight: "100vh" }}>
      {/* Header Controls */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        bgcolor: '#0e1729', 
        p: isMobile ? 1.5 : 2, 
        borderRadius: '8px', 
        border: '1px solid #253050',
        gap: 2
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: '#7c3aed', fontWeight: 'bold', textAlign: isMobile ? 'center' : 'left' }}>
          Advance Payment Report
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: isMobile ? 2 : 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto', width: isMobile ? '100%' : 'auto', pb: isMobile ? 0.5 : 0, justifyContent: 'center' }}>
            {[
              { label: "Month", value: "this_month" },
              { label: "Yesterday", value: "yesterday" },
              { label: "Today", value: "today" }
            ].map(p => (
              <Box key={p.value} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography sx={{ color: '#ff3333', fontSize: '9px', fontWeight: 'bold', mb: '2px', textTransform: 'uppercase' }}>
                  {p.label}
                </Typography>
                <Button
                  variant={activePeriod === p.value ? "contained" : "outlined"}
                  onClick={() => {
                    setActivePeriod(p.value);
                    fetchAdvanceData(p.value);
                  }}
                  sx={{
                    fontSize: '10px',
                    fontWeight: 'bold',
                    height: '26px',
                    minWidth: '70px',
                    color: activePeriod === p.value ? '#fff' : '#ff3333',
                    borderColor: '#ff3333',
                    bgcolor: activePeriod === p.value ? '#ff3333' : 'transparent',
                    '&:hover': { bgcolor: activePeriod === p.value ? '#cc2929' : 'rgba(255, 51, 51, 0.1)' }
                  }}
                >
                  view
                </Button>
              </Box>
            ))}
          </Box>

          {!isMobile && <Box sx={{ width: '1px', height: '32px', bgcolor: '#3a4a6b' }} />}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: isMobile ? '100%' : 'auto' }}>
            <Typography sx={{ color: '#8faac8', fontSize: '11px', fontWeight: 'bold' }}>Date:</Typography>
            <Box className="custom-datepicker" sx={{ flexGrow: isMobile ? 1 : 0 }}>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => {
                  setActivePeriod(null);
                  setSelectedDate(date);
                }}
                dateFormat="dd-MM-yyyy"
                customInput={
                  <TextField
                    size="small"
                    sx={{
                      bgcolor: '#1a2035',
                      '& .MuiOutlinedInput-root': {
                        color: '#e0e6f0',
                        fontSize: '12px',
                        height: '32px',
                        width: isMobile ? '100%' : '100px',
                        '& fieldset': { borderColor: '#3a4a6b' },
                      }
                    }}
                  />
                }
              />
            </Box>
            <IconButton onClick={() => fetchAdvanceData()} sx={{ color: '#7c3aed', bgcolor: '#1a2035', borderRadius: 1 }}>
                <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Report Container */}
      <Box sx={{ border: "1px solid #1c2b4d", borderRadius: "8px", overflow: "hidden", bgcolor: "#111c31" }}>
        <Box sx={{ py: 1.5, borderBottom: "1px solid #1c2b4d", textAlign: "center", bgcolor: "#000" }}>
          <Typography sx={{ color: "#ffd700", fontWeight: "bold", fontSize: isMobile ? "14px" : "16px", letterSpacing: "1px", textTransform: "uppercase" }}>
            Advance Data Report ( {dayjs(selectedDate).format("DD-MM-YYYY")} )
          </Typography>
        </Box>

        {isMobile ? (
          /* Mobile Card View */
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
            ) : data.length === 0 ? (
                <Typography align="center" sx={{ color: '#8899bb', py: 5 }}>No advance payments found</Typography>
            ) : (
                data.map((item) => (
                    <Card key={item.id} variant="outlined" sx={{ bgcolor: '#0e1729', borderColor: '#253050', color: '#fff' }}>
                        <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ color: '#7c3aed', fontWeight: 'bold', fontSize: '12px' }}>ID: {item.reservation?.booking_id || item.booking_id || 'N/A'}</Typography>
                                <Typography sx={{ color: '#ffd700', fontWeight: 'bold', fontSize: '12px' }}>Room: {item.room_number || '-'}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '15px', fontWeight: 'bold', mb: 0.5 }}>{item.check_in_guest_name || item.guest_name || 'N/A'}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ color: '#8faac8', fontSize: '11px' }}>{item.advance_type || 'ROOM ADVANCE'}</Typography>
                                <Chip label={item.pay_mode} size="small" sx={{ height: 18, fontSize: '9px', bgcolor: '#1a2035', color: '#00d7d7' }} />
                            </Box>
                            <Divider sx={{ my: 1, bgcolor: '#253050' }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                <Box>
                                    <Typography sx={{ color: '#8faac8', fontSize: '10px' }}>User: {formatUserName(item.user_name)}</Typography>
                                    <Typography sx={{ color: '#8faac8', fontSize: '10px' }}>{dayjs(item.date).format("DD-MM-YYYY")} {dayjs(`2000-01-01 ${item.time}`).format("hh:mm A")}</Typography>
                                </Box>
                                <Typography sx={{ color: '#00ff00', fontSize: '18px', fontWeight: 'bold' }}>
                                    ₹{item.advance_amount}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
          </Box>
        ) : (
          /* Desktop Table View */
          <TableContainer sx={{
            maxHeight: "calc(100vh - 350px)",
            '&::-webkit-scrollbar': { height: '8px', width: '8px' },
            '&::-webkit-scrollbar-track': { bgcolor: '#0e1729' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#3a4a6b', borderRadius: '4px' },
            bgcolor: "#111c31 !important"
          }}>
            <Table size="small" sx={{ bgcolor: "#111c31 !important" }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderStyle} align="left">R-SHIFT</TableCell>
                  <TableCell sx={tableHeaderStyle} align="center">Room No</TableCell>
                  <TableCell sx={tableHeaderStyle} align="left">Guest Name</TableCell>
                  <TableCell sx={tableHeaderStyle} align="left">Booking ID</TableCell>
                  <TableCell sx={tableHeaderStyle} align="left">Payment Type</TableCell>
                  <TableCell sx={{ ...tableHeaderStyle, bgcolor: '#1a2035' }} align="right">Amount</TableCell>
                  <TableCell sx={tableHeaderStyle} align="center">Pay Mode</TableCell>
                  <TableCell sx={tableHeaderStyle} align="left">User Name</TableCell>
                  <TableCell sx={tableHeaderStyle} align="left">Date & Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ bgcolor: "#111c31 !important" }}>
                {loading ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
                ) : data.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: '#8899bb' }}>No transactions found</TableCell></TableRow>
                ) : (
                  data.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => setSelectedRow(item.id)}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.1)' },
                        cursor: 'pointer',
                        bgcolor: selectedRow === item.id ? 'rgba(124, 58, 237, 0.2) !important' : 'transparent'
                      }}
                    >
                      <TableCell sx={{ ...cellStyle, textAlign: 'left' }}>
                        {item.shifted_from ? `${item.shifted_from} >>` : ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'center' }}>
                        {["RESERVATION ADVANCE", "RESERVATION UPDATE ADVANCE"].includes(String(item.advance_type || "").toUpperCase())
                          ? "-"
                          : (item.room_number || item.reservation?.room_number || "N/A")}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'left' }}>{item.check_in_guest_name || item.guest_name || item.reservation?.guest_name || 'N/A'}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'left' }}>{item.reservation?.booking_id || item.booking_id || 'N/A'}</TableCell>
                      <TableCell sx={{ ...cellStyle, fontSize: '10px', textAlign: 'left' }}>{item.advance_type || 'ROOM ADVANCE'}</TableCell>
                      <TableCell sx={{ ...cellStyle, fontWeight: 'bold', textAlign: 'right', color: '#00ff00' }}>₹{item.advance_amount}</TableCell>
                      <TableCell sx={{ ...cellStyle, fontWeight: 'bold', textAlign: 'center' }}>{item.pay_mode}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'left' }}>{formatUserName(item.user_name) || 'N/A'}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'left' }}>
                        {dayjs(item.date).format("DD-MM-YYYY")} <span style={{ color: '#fff', marginLeft: '8px' }}>{dayjs(`2000-01-01 ${item.time}`).format("hh:mm A")}</span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
